import React, { useLayoutEffect, useRef, useState } from 'react';

import Chart from 'react-apexcharts';
import { Empty } from 'antd';

import { formatters } from 'services';

type props = {
  data: { value: number; label: string }[];
  style?: React.CSSProperties;
  /** Optional explicit slice colors (aligned to `data` order). Lets callers
   *  keep the same label the same color across multiple pies. When omitted,
   *  ApexCharts assigns from its default palette by slice order (unchanged). */
  colors?: string[];
  /** Optional chart height passed to ApexCharts. A pie with no explicit height
   *  sizes itself to the container WIDTH; pass e.g. "100%" to make it fit a
   *  fixed-height slot instead (used by the stacked Long/Short pies). When
   *  omitted, ApexCharts' default ('auto') applies — unchanged for all callers. */
  height?: string | number;
  /** Hide ApexCharts' built-in legend. Defaults to shown (unchanged). Lets a
   *  caller render its own legend and center the bare pie in its container. */
  showLegend?: boolean;
};

const PieChart: React.FC<props> = ({
  data,
  style,
  colors,
  height,
  showLegend,
}) => {
  // "Fill the slot" mode. ApexCharts' own `height:'100%'` is a one-shot parent
  // measurement taken at mount — through a flex/% chain it reads the wrong value
  // depending on layout timing / DPR / zoom (pie ends up tiny on load, or
  // overflowing on other displays), and only self-corrects on a window resize.
  // Instead we observe the wrapper ourselves and feed ApexCharts an explicit
  // pixel height that tracks the slot on every resize/zoom/DPR change.
  const fill = height === '100%';
  const wrapRef = useRef<HTMLDivElement>(null);
  const [measuredH, setMeasuredH] = useState<number | undefined>(undefined);
  const hasData = data.length > 0;

  useLayoutEffect(() => {
    if (!fill) return undefined;
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => {
      const h = el.clientHeight;
      setMeasuredH(prev => (prev === h ? prev : h));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, [fill, hasData]);

  const series = data.map(item => item.value);
  const labels = data.map(item => item.label);

  if (!data.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  // In fill mode pass the measured pixel height; otherwise the caller's height
  // (fixed px or undefined) passes through unchanged.
  const chartHeight = fill ? measuredH : height;
  // Avoid a tiny first paint: in fill mode wait until we have a real height.
  const showChart = !fill || (measuredH != null && measuredH > 0);

  return (
    <div ref={wrapRef} style={style}>
      {showChart && (
        <Chart
          series={series}
          type="pie"
          {...(chartHeight != null ? { height: chartHeight } : {})}
          options={{
            labels,
            ...(colors && colors.length ? { colors } : {}),
            ...(showLegend === false ? { legend: { show: false } } : {}),
            chart: {
              animations: {
                enabled: false,
              },
            },
            responsive: [
              {
                breakpoint: 480,
                options: {
                  chart: {
                    width: 200,
                  },
                  legend: {
                    position: 'bottom',
                  },
                },
              },
            ],
            dataLabels: {
              formatter: (val: number) => `${val.toFixed(1)} %`,
            },
            tooltip: {
              y: {
                formatter: formatters.percent1Digit,
                title: {
                  formatter: (seriesName: string) => seriesName,
                },
              },
            },
          }}
        />
      )}
    </div>
  );
};

export default PieChart;
